import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE} from '../../../../styles/foundation/typography'
import {SUBTLE, BORDER, PRIMARY_BLUE, WARNING} from '../../../../styles/foundation/colors'
import {kg, int} from '../format'
import {round2} from '../weightMath'

/**
 * Weight Freeze (v2) — Initiate: the per-row weight sparkline.
 *
 * The two-colour rule, extracted so it is unit-tested without mounting the
 * SVG (see IniSpark.test.js). The MODAL bucket — the weight this row's own
 * `mpw` names — carries the primary colour; every other bucket is amber.
 * That mapping is load-bearing, not decorative: the explanatory copy above
 * the candidate table says "the amber bars in the distribution are exactly
 * those shipments", so WARNING is the one colour a non-modal bucket may
 * ever render as.
 *
 * Compared at the module's own 2dp precision (`round2`), the same defensive
 * choice Packing's sibling histogram (packing/components/DistributionBars.js)
 * makes, rather than a bare `===`. `mpw` is in practice copied verbatim off
 * the winning hist bucket by the generator's own `computeMpw` — never
 * recomputed via arithmetic — so the two floats are the same value in this
 * dataset either way; rounding first just means a future row whose `mpw`
 * DOES arrive through arithmetic (a merge, a re-derivation) still highlights
 * the right bar instead of silently losing its colour to float noise.
 */
export function barColor(weight, mpw) {
  return round2(weight) === round2(mpw) ? PRIMARY_BLUE : WARNING
}

/**
 * A 100×26 inline SVG histogram of one row's own charged weights: one bar
 * per observed weight, ordered light to heavy, height proportional to how
 * many shipments billed at it.
 *
 * Deliberately hand-rolled rather than drawn with Recharts (already a
 * dependency of this app): this renders ONCE PER TABLE ROW — up to a page's
 * worth at a time — and instantiating a charting library that many times
 * over is a serious performance regression for a sparkline this small, which
 * has no axes, legend or interaction to justify one. A charting library would
 * also drop the explicit per-bucket aria narration below in favour of a
 * canvas/SVG blob with no accessible content of its own — the accessibility
 * contract here is ONE `role="img"` node whose `aria-label` reads the entire
 * distribution, and the bars themselves carry no text.
 */
export default function IniSpark({hist, mpw}) {
  const h = (hist || []).slice().sort((a, b) => a[0] - b[0])
  if (!h.length) {
    return <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>No data</Typography>
  }

  const W = 100
  const H = 26
  const GAP = h.length > 1 ? 2 : 0
  const bw = (W - GAP * (h.length - 1)) / h.length
  const max = h.reduce((mx, x) => Math.max(mx, x[1]), 0) || 1
  const lines = h.map((x) => (
    kg(x[0]) + ': ' + int(x[1]) + (x[1] === 1 ? ' shipment' : ' shipments')
    + (round2(x[0]) === round2(mpw) ? ' (most probable)' : '')
  ))

  return (
    <Box
      component='svg'
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio='none'
      role='img'
      aria-label={'Charged weight distribution. ' + lines.join('. ') + '.'}
      sx={{width: '100%', maxWidth: 118, height: 26, display: 'block'}}
    >
      <title>{lines.join('\n')}</title>
      <rect x='0' y={H - 1} width={W} height='1' fill={BORDER} />
      {h.map((x, i) => {
        const bh = Math.max(2, (x[1] / max) * (H - 4))
        return (
          <rect
            key={x[0]}
            x={i * (bw + GAP)}
            y={H - 1 - bh}
            width={bw}
            height={bh}
            fill={barColor(x[0], mpw)}
          />
        )
      })}
    </Box>
  )
}
